/**
 * AssetHost for the in-browser test runner — bridges the sandbox's PER-BOOT
 * asset manager (the `ServerAssetManager` the test-utils `initializer` builds
 * and injects as every system's `$assets`, exposed as `environment.assetManager`)
 * to the @gotti/sandbox-test asset probe seam (`setAssetHost`), so generated
 * tests can assert "this sprite was actually drawn / this asset actually loaded".
 *
 * The manager keys its cache by FULL namespaced path (`client/images/x.png`),
 * but the sandbox is the client namespace and test authors + game code speak
 * BARE paths (`images/x.png`). The host translates: it presents bare paths to
 * the probe (strips a leading `client/`) and queries the manager with the full
 * namespaced form. `atlas.image` is authored bare, so it needs no translation.
 *
 * Create it AFTER the boot's manager is preloaded: it snapshots a reverse
 * image-object→path map and the AnimationAtlas index from the preloaded cache
 * (one scan per boot), then installs the access listener LAST so the
 * snapshotting reads don't pollute the "did game code touch this asset?" log.
 */
import type { BaseAssetManager } from '@gotti/core';
import { isAnimationAtlas, type AnimationAtlas } from '@gotti/sandbox-shared';
import type { AssetHost } from '@gotti/sandbox-core';

/**
 * The subset of the per-boot manager this host reads. `BaseAssetManager` (and
 * therefore its `ServerAssetManager` subclass, which `environment.assetManager`
 * is) satisfies it — typed structurally so the host stays decoupled from the
 * server package.
 */
export type AssetHostManager = Pick<
  BaseAssetManager,
  'list' | 'getImage' | 'getJSON' | 'has' | '__setAccessListener'
>;

/**
 * The runner-side host shape: AssetHost plus a host-only access-log reset.
 * Local extension (NOT on the sandbox-test AssetHost interface — the probe
 * seam never calls it): executeTests clears the log per spec file so
 * `wasAccessed` diagnostics reflect THAT spec's reads, not everything
 * accumulated across the whole run.
 */
export interface TestAssetHost extends AssetHost {
  clearAccessLog(): void;
}

const NS = 'client/';
/** Full namespaced manager key → bare probe-facing path. */
const toBare = (full: string): string => (full.startsWith(NS) ? full.slice(NS.length) : full);
/** Bare probe-facing path → full namespaced manager key. */
const toFull = (bare: string): string => (bare.startsWith(NS) ? bare : NS + bare);

export function createTestAssetHost(manager: AssetHostManager): TestAssetHost {
  // Reverse map: decoded image object → BARE asset path. getImage throws for
  // non-image assets (sounds, JSON) — guard and skip those.
  const imageToPath = new Map<unknown, string>();
  for (const full of manager.list()) {
    try {
      imageToPath.set(manager.getImage(full), toBare(full));
    } catch {
      /* not a decoded image — skip */
    }
  }

  // Atlas index: every valid AnimationAtlas under data/atlas/ (the generator's
  // contract location — see packages/sandbox-shared assetContracts). Malformed
  // JSON / non-atlas files fail open (skipped), so resolveFrame degrades to
  // null rather than throwing. `atlas.image` is authored bare, matched directly
  // against the probe's bare sheetPath.
  const atlases: AnimationAtlas[] = [];
  for (const full of manager.list(toFull('data/atlas'))) {
    try {
      const json = manager.getJSON(full);
      if (isAnimationAtlas(json)) atlases.push(json);
    } catch {
      /* unreadable / malformed — fail open */
    }
  }
  const atlasForSheet = (sheetPath: string): AnimationAtlas | undefined =>
    atlases.find((a) => a.image === sheetPath);

  // Access log — installed LAST so the scans above don't count as game reads.
  // The manager fires the listener with the FULL namespaced path (peek/require
  // key by it), so record BARE for the probe. Dies with the per-boot manager.
  const accessed = new Set<string>();
  manager.__setAccessListener((full: string) => accessed.add(toBare(full)));

  return {
    has: (path) => manager.has(toFull(path)),
    list: () => manager.list().map(toBare),
    wasAccessed: (path) => accessed.has(toBare(path)),
    resolveImage: (img) => imageToPath.get(img) ?? null,
    resolveFrame: (sheetPath, frame) => {
      const rect = atlasForSheet(sheetPath)?.frames?.[frame];
      if (!rect) return null;
      const { x, y, w, h } = rect;
      if (typeof x !== 'number' || typeof y !== 'number' || typeof w !== 'number' || typeof h !== 'number') return null;
      return { x, y, w, h };
    },
    listFrames: (sheetPath) => {
      const atlas = atlasForSheet(sheetPath);
      return atlas ? Object.keys(atlas.frames) : null;
    },
    clearAccessLog: () => accessed.clear(),
  };
}
